import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { PageHeader } from "@/components/site/PageHeader";
import { JsonLd } from "@/components/site/JsonLd";
import { supabase } from "@/integrations/supabase/client";
import { SHARE_IMAGES } from "@/lib/share-images";

export const Route = createFileRoute("/blog/$slug")({
  head: ({ params }) => {
    const title = params.slug
      .split("-")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
    const fullTitle = `${title} — Glow Long Golf Blog`;
    const desc = `${title} — night-golf insights and tips from Glow Long Golf.`;
    return {
      meta: [
        { title: fullTitle },
        { name: "description", content: desc },
        { property: "og:title", content: fullTitle },
        { property: "og:description", content: desc },
        { property: "og:url", content: `https://glowlonggolf.com/blog/${params.slug}` },
        { property: "og:type", content: "article" },
        { property: "og:image", content: SHARE_IMAGES.cometSwing },
        { name: "twitter:image", content: SHARE_IMAGES.cometSwing },
      ],
      links: [{ rel: "canonical", href: `https://glowlonggolf.com/blog/${params.slug}` }],
    };
  },

  component: BlogPost,
});

function BlogPost() {
  const { slug } = Route.useParams();
  const { data, isLoading } = useQuery({
    queryKey: ["blog", slug],
    queryFn: async () => {
      const { data } = await supabase.from("blog_posts").select("*").eq("slug", slug).eq("is_published", true).maybeSingle();
      return data;
    },
  });
  if (isLoading) return <div className="mx-auto max-w-3xl px-4 py-20 text-muted-foreground">Loading…</div>;
  if (!data) throw notFound();
  const post: any = data;
  // Render markdown as plain paragraphs — simple, no markdown lib needed.
  const blocks = post.body_md.split(/\n\n+/);
  return (
    <>
      <JsonLd data={{ "@context": "https://schema.org", "@type": "Article", headline: post.title, datePublished: post.published_at, author: { "@type": "Organization", name: "Glow Long Golf" } }} />
      <PageHeader eyebrow="Journal" title={post.title} lead={post.excerpt ?? undefined} />
      <article className="mx-auto max-w-3xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="space-y-4 text-muted-foreground">
          {blocks.map((b: string, i: number) => {
            if (b.startsWith("# ")) return <h1 key={i} className="text-foreground" />; // already in header
            if (b.startsWith("## ")) return <h2 key={i} className="mt-8 font-display text-2xl font-semibold text-foreground">{b.replace(/^##\s*/, "")}</h2>;
            if (b.startsWith("- ")) return (
              <ul key={i} className="ml-5 list-disc space-y-1">
                {b.split("\n").map((li, j) => <li key={j}>{li.replace(/^[-*]\s*/, "")}</li>)}
              </ul>
            );
            return <p key={i}>{b}</p>;
          })}
        </div>
        <div className="mt-10">
          <Link to="/blog" className="text-sm font-semibold text-primary hover:underline">← Back to blog</Link>
        </div>
      </article>
    </>
  );
}
